import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2, RefreshCw, TrendingUp, TrendingDown, Minus, AlertTriangle, CheckCircle2, Crosshair } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { apiError } from "@/lib/api";

const TREND = {
  improving: { icon: TrendingUp, color: "text-emerald-400", label: "Improving" },
  steady: { icon: Minus, color: "text-amber-400", label: "Steady" },
  declining: { icon: TrendingDown, color: "text-red-400", label: "Declining" },
};

export const CoachingDigest = () => {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/analytics/coaching-digest").then(({ data }) => { if (data?.digest) setDoc(data); }).catch(() => {}); }, []);

  const generate = async (force = false) => {
    setLoading(true);
    try {
      const { data } = await api.post(`/analytics/coaching-digest${force ? "?force=true" : ""}`);
      setDoc(data);
      toast.success("Coaching digest ready");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not generate digest");
    } finally { setLoading(false); }
  };

  const d = doc?.digest;
  const trend = d ? TREND[d.overall_trend] || TREND.steady : null;

  return (
    <div className="glass rounded-2xl p-6" data-testid="coaching-digest-card">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4 text-violet-400" /> Weekly Coaching Digest</h3>
          <p className="text-xs text-slate-500 mt-0.5">AI review of your recurring mistakes and best habits from this week's graded trades.</p>
        </div>
        {d && (
          <button onClick={() => generate(true)} disabled={loading} className="text-xs text-slate-400 hover:text-violet-400 flex items-center gap-1 transition-colors shrink-0" data-testid="digest-regenerate-button">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        )}
      </div>

      {!d && (
        <div className="text-center py-8">
          <p className="text-sm text-slate-400 mb-4">No digest yet for this week. Your coach will review every graded trade from the last 7 days.</p>
          <Button onClick={() => generate(false)} disabled={loading} data-testid="digest-generate-button" className="bg-violet-500 hover:bg-violet-400 text-white font-semibold">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reviewing your trades…</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate this week's coaching digest</>}
          </Button>
        </div>
      )}

      {d && (
        <div className="space-y-5 fade-up" data-testid="digest-content">
          <div className="flex flex-wrap items-center gap-3">
            <span className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-2.5 py-1 bg-white/5 border border-white/10 ${trend.color}`} data-testid="digest-trend">
              <trend.icon className="w-3.5 h-3.5" /> {trend.label}
            </span>
            <span className="text-xs text-slate-500 font-mono-num">{doc.trades_reviewed} trades · {doc.period_start} → {doc.period_end}</span>
            <span className="text-xs text-slate-500 font-mono-num">Grades: {doc.grades?.join(" ")}</span>
          </div>
          <p className="font-display text-lg font-bold leading-snug" data-testid="digest-headline">{d.headline}</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <List title="Recurring mistakes" icon={AlertTriangle} color="text-red-400" items={d.recurring_mistakes} testId="digest-mistakes" />
            <List title="Best habits" icon={CheckCircle2} color="text-emerald-400" items={d.best_habits} testId="digest-habits" />
          </div>
          <List title="Focus next week" icon={Crosshair} color="text-cyan-400" items={d.focus_next_week} testId="digest-focus" numbered />
          <p className="text-sm text-slate-300 leading-relaxed border-l-2 border-violet-500/60 pl-3" data-testid="digest-note">{d.coach_note}</p>
        </div>
      )}
    </div>
  );
};

function List({ title, icon: Icon, color, items = [], testId, numbered }) {
  return (
    <div data-testid={testId}>
      <p className={`text-xs uppercase tracking-wider mb-2 flex items-center gap-1.5 ${color}`}><Icon className="w-3.5 h-3.5" /> {title}</p>
      <ul className="space-y-1.5 text-sm text-slate-300">
        {items.map((x, i) => <li key={i} className="flex gap-2"><span className="text-slate-500 font-mono-num shrink-0">{numbered ? `${i + 1}.` : "•"}</span><span>{x}</span></li>)}
      </ul>
    </div>
  );
}
